import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, useBlocker, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Download, MessageSquare } from '@repo/media/lucide';

import { Badge } from '@repo/components/badge';
import { Button } from '@repo/components/button';
import { Card } from '@repo/components/card';
import { DialogTrigger, ModalDialog } from '@repo/components/dialog';
import { Popover } from '@repo/components/popover';

import { addToast } from '@repo/features/toast';
import { formatDateTime } from '@repo/utils/format-date';

import { useFeatureGuard } from '~/features/auth/use-feature-guard.ts';
import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { PageError } from '~/features/page-error/page-error.tsx';
import {
  deleteDeclaration,
  getDeclarationPdfPreviewUrl,
  getDeclarationPdfUrl,
  queryDeclarationForm,
  queryValidation,
  submitDeclaration,
  submitPreliminaryDeclaration,
  updateDeclaration,
  updatePreliminaryDeclaration,
} from '~/services/declaration/declaration.ts';
import type { ComponentField, ComponentValueUpdate, DeclarationForm } from '~/services/declaration/types.ts';
import { NotFoundError } from '~/services/http.ts';

import { CancelDeclarationDialog } from './-components/cancel-declaration-dialog.tsx';
import { DeclarationForm as DeclarationFormComponent } from './-components/declaration-form.tsx';
import { DeclarationStatusBadge } from './-components/declaration-status-badge.tsx';
import { ValidationSummary } from './-components/validation-summary.tsx';
import { DEFAULT_SEARCH_PARAMS } from './-consts.ts';

type SaveDeclarationMutationVariables = {
  values: ComponentValueUpdate[];
  silentSuccessToast?: boolean;
  generalNote?: string | null;
};

export const Route = createFileRoute(
  '/project/$project/(declarations)/declarations/$declaration',
)({
  component: DeclarationDetail,
  loader: async ({ context: { queryClient }, params: { declaration, project } }) => {
    try {
      await Promise.all([
        queryClient.ensureQueryData(queryDeclarationForm(project, declaration)),
        queryClient.ensureQueryData(queryValidation(project, declaration)),
      ]);
    }
    catch (e) {
      if (e instanceof NotFoundError) {
        throw notFound();
      }
      throw e;
    }
  },
  notFoundComponent: () => (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <h1 className="text-2xl font-semibold text-secondary-800">Declaration Not Found</h1>
      <p className="text-secondary-500">The declaration you're looking for doesn't exist or has been removed.</p>
    </div>
  ),
  errorComponent: () => <PageError msg="An error occurred while loading the declaration." />,
});

function formatUnit(unit: ComponentField['unit']) {
  if (unit === 'Micrometer')
    return 'μm';
  if (unit === 'PpmWw')
    return 'ppm w/w';
  return 'ppm-mol';
}

function formatLimit(max: number | null, unit: string): string | null {
  if (max !== null) {
    return `≤ ${formatValue(max)} ${unit}`;
  }
  return null;
}

function formatValue(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }).format(value);
}

function formatPurity(value: number | null): string {
  if (value === null) {
    return '–';
  }

  return `${new Intl.NumberFormat(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value)} mol%`;
}

function renderDisplayValue(field: ComponentField) {
  const unit = formatUnit(field.unit);

  if (field.inputType === 'Boolean') {
    return field.qualifier === 'Detected' ? 'Detected' : 'Not detected';
  }

  if (field.qualifier === 'NotDetected') {
    return field.detectionLimit !== null
      ? `Not detected (LOD ${formatValue(field.detectionLimit)} ${unit})`
      : 'Not detected';
  }

  if (field.qualifier === 'Detected') {
    return field.quantificationLimit !== null
      ? `Detected (LOQ ${formatValue(field.quantificationLimit)} ${unit})`
      : 'Detected';
  }

  if (field.value !== null) {
    return `${formatValue(field.value)} ${unit}`;
  }

  if (field.isBasisBacked) {
    return 'Basis';
  }

  return '–';
}

function ReadOnlyNoteButton({ componentName, note }: { componentName: string; note: string }) {
  return (
    <DialogTrigger>
      <Button
        variant="ghost"
        size="icon-sm"
        icon={{ component: <MessageSquare size={16} /> }}
        className="shrink-0 text-primary-600"
        aria-label={`View note for ${componentName}`}
      />
      <Popover placement="bottom start">
        <div className="p-3 max-w-72">
          <p className="whitespace-pre-line text-sm">{note}</p>
        </div>
      </Popover>
    </DialogTrigger>
  );
}

function BasisInfoButton({ field }: { field: ComponentField }) {
  if (!field.isBasisBacked) {
    return null;
  }

  return (
    <DialogTrigger>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`View basis information for ${field.componentName}`}
        className="h-auto min-h-0 rounded-full p-0 hover:bg-transparent"
      >
        <Badge color="info">Basis</Badge>
      </Button>
      <Popover placement="bottom start">
        <div className="max-w-80 p-3 space-y-2 text-sm">
          <p className="font-medium">Basis-backed value</p>
          {field.basisSummary && <p className="text-secondary-700">{field.basisSummary}</p>}
          {field.basisDocumentReference && (
            <a
              href={field.basisDocumentReference}
              target="_blank"
              rel="noreferrer"
              className="text-primary-700 hover:underline"
            >
              View reference
            </a>
          )}
        </div>
      </Popover>
    </DialogTrigger>
  );
}

function DeclarationDetail() {
  const { declaration: declarationId, project } = Route.useParams();
  const queryClient = useQueryClient();

  const formQuery = useSuspenseQuery(queryDeclarationForm(project, declarationId));
  const validationQuery = useSuspenseQuery(queryValidation(project, declarationId));
  const form = formQuery.data;
  const validation = validationQuery.data;
  const declarationTypeLabel = form.isPreliminary ? 'Preliminary declaration' : 'Final declaration';

  const navigate = useNavigate();
  const hasUnsavedChangesRef = useRef(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const setDirty = useCallback((isDirty: boolean) => {
    hasUnsavedChangesRef.current = isDirty;
  }, []);

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => hasUnsavedChangesRef.current,
    enableBeforeUnload: () => hasUnsavedChangesRef.current,
    withResolver: true,
  });

  const saveMutation = useMutation({
    mutationFn: ({ values, generalNote }: SaveDeclarationMutationVariables) => form.isPreliminary
      ? updatePreliminaryDeclaration(project, declarationId, values, generalNote)
      : updateDeclaration(project, declarationId, values, generalNote),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['declaration', project, declarationId] }),
        queryClient.invalidateQueries({ queryKey: ['declaration', project, declarationId, 'form'] }),
        queryClient.invalidateQueries({ queryKey: ['declaration', project, declarationId, 'validation'] }),
      ]);
      if (!variables.silentSuccessToast) {
        addToast({ variant: 'positive', title: 'Declaration saved' });
      }
      setDirty(false);
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to save declaration',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: () => form.isPreliminary
      ? submitPreliminaryDeclaration(project, declarationId)
      : submitDeclaration(project, declarationId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['declaration', project, declarationId] }),
        queryClient.invalidateQueries({ queryKey: ['declaration', project, declarationId, 'form'] }),
        queryClient.invalidateQueries({ queryKey: ['declaration', project, declarationId, 'validation'] }),
        queryClient.invalidateQueries({ queryKey: [project, 'declarations'] }),
      ]);
      addToast({ variant: 'positive', title: `${declarationTypeLabel} submitted successfully` });
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to submit declaration',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteDeclaration(project, declarationId),
    onSuccess: () => {
      setDirty(false);
      void queryClient.invalidateQueries({ queryKey: [project, 'declarations'] });
      queryClient.removeQueries({ queryKey: ['declaration', project, declarationId] });
      addToast({ variant: 'positive', title: 'Declaration cancelled' });
      void navigate({
        to: '/project/$project/declarations',
        params: { project },
        search: DEFAULT_SEARCH_PARAMS,
      });
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to cancel declaration',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const pdfMutation = useMutation({
    mutationFn: () => getDeclarationPdfUrl(project, declarationId),
    onSuccess: (url) => {
      window.open(url, '_blank');
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to download PDF',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const previewPdfMutation = useMutation({
    mutationFn: () => getDeclarationPdfPreviewUrl(project, declarationId),
    onSuccess: (url) => {
      window.open(url, '_blank');
    },
    onError: (error) => {
      addToast({
        variant: 'negative',
        title: 'Failed to generate PDF preview',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  const { hasFeature } = useFeatureGuard();
  const canDeclare = hasFeature({ key: 'composition:declare', scope: 'Project' });
  const isReadOnly = form.status !== 'Draft' || !canDeclare;

  const saveDeclaration = useCallback(
    async (
      values: ComponentValueUpdate[],
      options?: { silentSuccessToast?: boolean; generalNote?: string | null },
    ) => {
      const variables: SaveDeclarationMutationVariables = { values };
      if (options?.silentSuccessToast !== undefined) {
        variables.silentSuccessToast = options.silentSuccessToast;
      }
      if (options?.generalNote !== undefined) {
        variables.generalNote = options.generalNote;
      }
      await saveMutation.mutateAsync(variables);
    },
    [saveMutation],
  );

  return (
    <div className="@container flex flex-col items-start gap-6 pb-6 max-w-6xl mx-auto w-full">
      <div
        className={twMerge(
          'sticky top-0 z-20 bg-primary-contrast/95 backdrop-blur',
          'flex flex-col gap-4 w-full',
          '@lg:flex-row @lg:justify-between',
          'pt-2 pb-4 border-b border-b-secondary-200',
        )}
      >
        <div className="space-y-1">
          <ContentHeader back={{ to: '..' }}>Declaration</ContentHeader>
          {form.cargoId
            ? (
                <p className="text-secondary-500">
                  Cargo:
                  {' '}
                  {form.cargoId}
                </p>
              )
            : (
                <p className="text-secondary-500">Cargo: –</p>
              )}
        </div>
        <div className={twMerge('flex flex-wrap gap-4 items-end', '@lg:gap-6')}>
          <div>
            <p className="text-secondary-500 text-sm">State</p>
            <DeclarationStatusBadge status={form.status} />
          </div>
          <div>
            <p className="text-secondary-500 text-sm">Type</p>
            <Badge color={form.isPreliminary ? 'secondary' : 'info'}>
              {form.isPreliminary ? 'Preliminary' : 'Final'}
            </Badge>
          </div>
          {form.voyageReferenceNo && (
            <div>
              <p className="text-secondary-500 text-sm">Voyage</p>
              <p className="font-mono">{form.voyageReferenceNo}</p>
            </div>
          )}
          <div>
            <p className="text-secondary-500 text-sm">Estimated CO2 purity (upper bound)</p>
            <p>{formatPurity(form.estimatedCo2Purity)}</p>
          </div>
          {form.declaredByName && (
            <div>
              <p className="text-secondary-500 text-sm">Declared by</p>
              <p>{form.declaredByName}</p>
            </div>
          )}
          {form.status === 'Declared' && (
            <div>
              <p className="text-secondary-500 text-sm">Spec compliance</p>
              <Badge color={validation.isWithinSpec ? 'positive' : 'negative'}>
                {validation.isWithinSpec ? 'Within spec' : 'Off spec'}
              </Badge>
            </div>
          )}
          {form.declaredAt && (
            <div>
              <p className="text-secondary-500 text-sm">Declared on</p>
              <p>{formatDateTime(form.declaredAt)}</p>
            </div>
          )}
          {form.status === 'Declared' && (
            <Button
              variant="outline"
              size="sm"
              icon={{ component: <Download size={14} /> }}
              action={() => { pdfMutation.mutate(); }}
              isPending={pdfMutation.isPending}
            >
              Download PDF
            </Button>
          )}
        </div>
      </div>

      {isReadOnly
        ? (
            <ReadOnlyView
              form={form}
              validation={validation}
              isValidationRefreshing={validationQuery.isFetching || formQuery.isFetching || submitMutation.isPending}
            />
          )
        : (
            <DeclarationFormComponent
              form={form}
              isPreliminary={Boolean(form.isPreliminary)}
              onSave={saveDeclaration}
              onSubmit={async () => {
                await submitMutation.mutateAsync();
              }}
              isSaving={saveMutation.isPending}
              isSubmitting={submitMutation.isPending}
              onDirtyChange={setDirty}
              onPreviewPdf={() => { previewPdfMutation.mutate(); }}
              isPreviewingPdf={previewPdfMutation.isPending}
              {...(form.status === 'Draft' && {
                onCancel: () => { setShowCancelDialog(true); },
                isCancelling: deleteMutation.isPending,
              })}
            />
          )}

      <ModalDialog
        isOpen={status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) {
            reset?.();
          }
        }}
        title="Unsaved Changes"
        role="alertdialog"
      >
        <div className="flex flex-col gap-6">
          <p>
            You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
          </p>
          <div className="flex justify-end gap-4">
            <Button color="primary" variant="soft" action={() => { reset?.(); }}>
              Stay on Page
            </Button>
            <Button color="destructive" action={() => { proceed?.(); }}>
              Leave Without Saving
            </Button>
          </div>
        </div>
      </ModalDialog>

      <CancelDeclarationDialog
        isOpen={showCancelDialog}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setShowCancelDialog(false);
          }
        }}
        onConfirm={() => { deleteMutation.mutate(); }}
        isPending={deleteMutation.isPending}
        cargoId={form.cargoId ?? null}
      />
    </div>
  );
}

function ReadOnlyView({
  form,
  validation,
  isValidationRefreshing,
}: {
  form: DeclarationForm;
  validation: import('~/services/declaration/types.ts').ValidationResponse;
  isValidationRefreshing: boolean;
}) {
  const outOfSpecKeys = useMemo(() => new Set(
    validation.componentResults
      .filter(r => !r.isWithinSpec)
      .map(r => r.componentKey),
  ), [validation.componentResults]);

  return (
    <div className="flex flex-col gap-6 w-full">
      {!isValidationRefreshing && <ValidationSummary validation={validation} />}

      {form.generalNote && (
        <Card>
          <div className="p-4 space-y-1">
            <p className="text-sm font-medium text-secondary-700">General Note</p>
            <p className="text-secondary-600 whitespace-pre-line">{form.generalNote}</p>
          </div>
        </Card>
      )}

      <Card>
        <div className="divide-y divide-secondary-200">
          {form.fields.map((field) => {
            const isOutOfSpec = outOfSpecKeys.has(field.componentKey);
            const unit = formatUnit(field.unit);
            const limitText = formatLimit(field.maxValue, unit);

            return (
              <div
                key={field.declarationItemId}
                className={twMerge(
                  'grid gap-2 py-3 items-center',
                  'grid-cols-[1fr_auto] @md:grid-cols-[1fr_auto_auto]',
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{field.componentName}</span>
                  <span className="text-secondary-500 text-sm shrink-0">{field.componentCode}</span>
                  {isOutOfSpec && (
                    <Badge color="warning">Out of spec</Badge>
                  )}
                  <BasisInfoButton field={field} />
                  {field.note && (
                    <ReadOnlyNoteButton componentName={field.componentName} note={field.note} />
                  )}
                </div>

                <div className="hidden @md:block text-secondary-500 text-sm text-right min-w-32">
                  {limitText ?? '—'}
                </div>

                <div className="text-right min-w-28">
                  <span className={twMerge(
                    'font-medium',
                    isOutOfSpec && 'text-amber-700',
                  )}
                  >
                    {renderDisplayValue(field)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
